import React, { useEffect, useState } from 'react';
import { clinicService } from '../services/clinicService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

function Staff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    loadStaff();
  }, [currentPage, searchTerm]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await clinicService.getStaff({ page: currentPage, search: searchTerm });
      setStaff(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      await clinicService.createStaff(data);
      toast.success('Staff member added successfully!');
      setShowModal(false);
      reset();
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add staff member');
    }
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setValue('name', staffMember.name);
    setValue('email', staffMember.email);
    setValue('mobile', staffMember.mobile);
    setValue('role', staffMember.role);
    setValue('address', staffMember.address || '');
    setShowEditModal(true);
  };

  const onEditSubmit = async (data) => {
    try {
      const updateData = { ...data };
      if (!updateData.password) {
        delete updateData.password;
      }
      await clinicService.updateStaff(selectedStaff.id, updateData);
      toast.success('Staff member updated successfully!');
      setShowEditModal(false);
      reset();
      setSelectedStaff(null);
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update staff member');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await clinicService.deleteStaff(id);
        toast.success('Staff member deleted successfully');
        loadStaff();
      } catch (error) {
        toast.error('Failed to delete staff member');
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading && staff.length === 0) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">👔 Staff Members</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          ➕ Add Staff
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="🔍 Search by name, mobile, or email..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full max-w-md px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {staff.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                  No staff members found. Add your first staff member!
                </td>
              </tr>
            ) : (
              staff.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{member.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      member.role === 'receptionist' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {member.role === 'receptionist' ? '📋 Receptionist' : '🩺 Nurse'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{member.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{member.mobile}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleEdit(member)}
                      className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} staff members
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 border rounded-lg bg-blue-50 text-blue-600 font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
              disabled={currentPage === pagination.last_page}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Add New Staff Member</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input {...register('name', { required: 'Name is required' })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.name && <span className="text-red-500 text-xs">{errors.name.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input type="email" {...register('email', { required: 'Email is required' })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input {...register('mobile', { required: 'Mobile is required' })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.mobile && <span className="text-red-500 text-xs">{errors.mobile.message}</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role *</label>
                  <select {...register('role', { required: 'Role is required' })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Role</option>
                    <option value="receptionist">Receptionist</option>
                    <option value="nurse">Nurse</option>
                  </select>
                  {errors.role && <span className="text-red-500 text-xs">{errors.role.message}</span>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <input type="password" {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.password && <span className="text-red-500 text-xs">{errors.password.message}</span>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <textarea {...register('address')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold">
                  Add Staff Member
                </button>
                <button type="button" onClick={() => { setShowModal(false); reset(); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Edit Staff Member</h2>
            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input {...register('name', { required: 'Name is required' })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input type="email" {...register('email', { required: 'Email is required' })} className="w-full px-3 py-2 border rounded-lg" disabled title="Email cannot be changed" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input {...register('mobile', { required: 'Mobile is required' })} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role *</label>
                  <select {...register('role', { required: 'Role is required' })} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Role</option>
                    <option value="receptionist">Receptionist</option>
                    <option value="nurse">Nurse</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">New Password (leave blank to keep current)</label>
                  <input type="password" {...register('password', { minLength: { value: 6, message: 'Password must be at least 6 characters' } })} className="w-full px-3 py-2 border rounded-lg" />
                  {errors.password && <span className="text-red-500 text-xs">{errors.password.message}</span>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <textarea {...register('address')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold">
                  Update Staff Member
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); reset(); setSelectedStaff(null); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Staff;
